import type { Client, ClientHeaders } from "@medusajs/js-sdk";

import type { HttpTypes } from "@medusajs/types";
import type { SalonStockerHttpTypes } from "../types";

export class Category {
  /**
   * @ignore
   */
  private client: Client;
  /**
   * @ignore
   */
  constructor(client: Client) {
    this.client = client;
  }

  /**
   * This method retrieves a list of product categories. It sends a request to the
   * [List Product Categories API route](https://docs.medusajs.com/api/store#product-categories_getproductcategories).
   *
   * @param query - Filters and pagination configurations.
   * @param headers - Headers to pass in the request.
   * @returns The paginated list of product categories.
   */
  list = async (
    query?: HttpTypes.StoreProductCategoryListParams,
    headers?: ClientHeaders
  ): Promise<HttpTypes.StoreProductCategoryListResponse> => {
    return await this.client.fetch<HttpTypes.StoreProductCategoryListResponse>(
      `/store/product-categories`,
      {
        method: "GET",
        query,
        headers
      }
    );
  };

  /**
   * This method is used to retrieve a product category by its ID. It sends a request to the
   * [Get Product Category](https://docs.medusajs.com/api/store#product-categories_getproductcategoriesid) API route.
   *
   * @param id - The product category's ID.
   * @param query - Configure the fields to retrieve in the product category.
   * @param headers - Headers to pass in the request
   * @returns The product category.
   */
  retrieve = async (
    id: string,
    query?: HttpTypes.StoreProductCategoryParams,
    headers?: ClientHeaders
  ): Promise<HttpTypes.StoreProductCategoryResponse> => {
    return await this.client.fetch<HttpTypes.StoreProductCategoryResponse>(
      `/store/product-categories/${id}`,
      {
        method: "GET",
        query,
        headers
      }
    );
  };

  /**
   * This method retrieves product counts for highlighted categories. It sends a request to the
   * `/store/product-categories-count` API route.
   *
   * @param headers - Headers to pass in the request.
   * @returns List of categories with their product counts.
   *
   * @example
   * To retrieve category product counts:
   *
   * ```ts
   * sdk.store.category.getProductCounts()
   * .then(({ categories }) => {
   *   console.log(categories)
   * })
   * ```
   *
   * @example
   * Response structure:
   *
   * ```ts
   * {
   *   categories: [
   *     {
   *       category_id: "pcat_123",
   *       handle: "sneakers",
   *       name: "Sneakers",
   *       mpath: "pcat_123",
   *       product_count: 15
   *     }
   *   ]
   * }
   * ```
   */
  getProductCounts = async (
    headers?: ClientHeaders
  ): Promise<SalonStockerHttpTypes.StoreProductCategoriesCountResponse> => {
    return await this.client.fetch<SalonStockerHttpTypes.StoreProductCategoriesCountResponse>(
      `/store/product-categories-count`,
      {
        method: "GET",
        headers
      }
    );
  };
}
